import React, { useEffect } from 'react';
import { Outlet, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from 'src/redux/store';
import SurveyContext from 'src/contexts/SurveyContext';
import {
  fetchSurveyMeta,
  selectSurveyMeta,
} from 'src/redux/slices/Survey/surveyMetaSlice';
import useAuth from 'src/hooks/useAuth';

export function SurveyLayout(): React.JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const { surveyId: surveyIdParam, memberId } = useParams<{
    surveyId: string;
    memberId?: string;
  }>();
  const { user: authUser } = useAuth();

  const surveyId = surveyIdParam ? parseInt(surveyIdParam, 10) : 0;
  const actingOnMemberId = memberId ? parseInt(memberId, 10) : null;
  const targetUserId = actingOnMemberId ?? authUser?.id ?? 0;
  const isOnBehalf = !!actingOnMemberId;

  const metaEntry = useSelector(selectSurveyMeta(surveyId));

  useEffect(() => {
    if (!surveyId) {
      return;
    }
    if (
      metaEntry?.status === 'loading' ||
      metaEntry?.status === 'succeeded' ||
      metaEntry?.status === 'failed'
    ) {
      return;
    }
    dispatch(fetchSurveyMeta({ surveyId, actingOnMemberId }));
  }, [dispatch, surveyId, actingOnMemberId, metaEntry?.status]);

  const data = metaEntry?.data ?? null;

  return (
    <SurveyContext.Provider
      value={{
        surveyId,
        targetUserId,
        actingOnMemberId,
        isOnBehalf,
        certId: data?.cert_id ?? null,
        certName: data?.cert_name ?? null,
        propertyName: data?.property_name ?? null,
        certCategory: data?.cert_category ?? null,
        effectiveDate: data?.effective_date ?? null,
        isProfile: data?.is_profile ?? false,
        memberName: data?.member_name ?? null,
        isLockedByProperty: data?.renter_active === false,
        renterActive: data?.renter_active ?? false,
      }}
    >
      <div
        data-dd-privacy="mask-user-input"
        role="region"
        aria-label="Survey"
        style={{ height: '100%' }}
      >
        <Outlet />
      </div>
    </SurveyContext.Provider>
  );
}

export default SurveyLayout;
